import React, { Component, forwardRef, Fragment, ReactNode } from "react";

import {
  clearSelectionId,
  emptyStateItemId,
  SelectDataItem,
  SelectGroupData,
} from "./common/utils";
import { ItemClear } from "./items/item-clear.component";
import { ItemSingle } from "./items/item-single.component";

export type CpSelectContentProps = {
  data: SelectDataItem[] | SelectGroupData[];
  disableSelection?: boolean;
  disabled?: boolean;
  isGroupData?: boolean;
  groupType?: "header" | "border";
  isMulti?: boolean;
  onChange?: (item: SelectDataItem, isMulti?: boolean) => void;
  renderGroup?: React.ComponentType<any>;
  renderItem?: React.ComponentType<any>;
  searchOnChange?: (value: string) => void;
  searchValue?: string;
  selectedId?: string;
  selectItem?: (item: SelectDataItem) => void;
  showResultsInContent?: boolean;
  valueTransformed?: SelectDataItem | SelectDataItem[];
  clearable?: boolean;
  renderEmptyState?: (params: any) => ReactNode;
  allowTextWrap?: boolean;
};

export const CpSelectContent = forwardRef<HTMLDivElement, CpSelectContentProps>(
  function CpSelectContent(props, ref) {
    const {
      data,
      disableSelection,
      disabled,
      isGroupData,
      groupType,
      isMulti,
      onChange,
      renderGroup,
      renderItem,
      searchOnChange,
      searchValue,
      selectedId,
      selectItem,
      showResultsInContent,
      valueTransformed,
      clearable,
      renderEmptyState,
      allowTextWrap,
    } = props;

    function buildItem(item: SelectDataItem) {
      const Item = renderItem;
      const itemProps = {
        disableSelection,
        disabled,
        isSelected: item.id.toString() === selectedId,
        item,
        key: item.id,
        onChange,
        searchOnChange,
        searchValue,
        selectItem,
        value: valueTransformed,
        allowTextWrap,
      };
      if (item.id === clearSelectionId) {
        return <ItemClear {...itemProps} />;
      }
      if (item.id === emptyStateItemId) {
        return renderEmptyState?.({
          itemProps,
          itemId: emptyStateItemId,
          renderSingleItem: (
            name: string,
            subName?: string,
            overline?: string,
          ) => {
            return (
              <ItemSingle
                {...itemProps}
                value={
                  !isMulti ? (valueTransformed as SelectDataItem) : undefined
                }
                item={{
                  id: emptyStateItemId,
                  name,
                  subName,
                  overline,
                }}
              />
            );
          },
        });
      }
      if (!Item) return null;
      return <Item {...itemProps} />;
    }

    const hasData = !!data.length;
    let hasValue: any, showEmptyState, showSelected, shouldShowClearRow;
    if (isMulti) {
      const valueArray =
        (valueTransformed as SelectDataItem[] | undefined) ?? [];
      hasValue = valueArray.length;
      showEmptyState =
        !hasData &&
        (searchValue || !valueArray.length || !showResultsInContent);
      showSelected = !hasData && !!valueArray.length && showResultsInContent;
    } else {
      hasValue = !!valueTransformed;
      shouldShowClearRow = hasValue && clearable;
      showEmptyState = !hasData && !shouldShowClearRow;
    }
    return (
      <>
        {!showEmptyState || renderEmptyState ? (
          <div className="cp-select-component__content-items" ref={ref}>
            {!!(hasValue || hasData) && (
              <>
                {shouldShowClearRow &&
                  buildItem({ id: clearSelectionId, name: "Clear selection" })}
                {showSelected &&
                  (
                    (valueTransformed as SelectDataItem[] | undefined) ?? []
                  ).map((item) => buildItem(item))}
                {isGroupData
                  ? (data as SelectGroupData[]).map((group, index) => {
                      const RenderGroup = renderGroup;
                      const isFirstSeparator =
                        groupType === "border" && index === 0;
                      return (
                        <Fragment key={group.id}>
                          {!isFirstSeparator && RenderGroup && (
                            <RenderGroup
                              {...group}
                              count={group.data.length}
                              isSeparator={groupType === "border"}
                            />
                          )}
                          {group.data.map(buildItem)}
                        </Fragment>
                      );
                    })
                  : (data as SelectDataItem[]).map(buildItem)}
              </>
            )}
            {renderEmptyState &&
              !hasData &&
              buildItem({ id: emptyStateItemId, name: "" })}
          </div>
        ) : (
          <div className="cp-select-component__empty">
            <div className="cp-select-component__empty-text">No Results</div>
            Please refine your search
          </div>
        )}
      </>
    );
  },
);

export type RenderContentProps = {
  shouldUpdate?: boolean;
  children?: ReactNode;
};

export class RenderContent extends Component<RenderContentProps> {
  shouldComponentUpdate(nextProps: RenderContentProps) {
    return !!nextProps.shouldUpdate;
  }
  render() {
    return this.props.children;
  }
}
